/*
 * Dark blocks marked `data-stack` pin while the following section slides up
 * over them, dimming as they are covered.
 *
 * Cards that fit the viewport pin at the top (`--stack-top: 0`). Taller cards
 * get a negative offset so their content can scroll through before they pin —
 * otherwise the next section covers the bottom (e.g. the contact submit).
 *
 * `data-stack` may carry a 0–1 align: the card pins once its submit (or
 * bottom edge) reaches that fraction of the viewport. Contact uses 0.6 so
 * the submit can rise to mid-screen before the footer covers it. A bare
 * `data-stack` aligns to 1 (the viewport bottom).
 *
 * The hero only pins when its content actually fits the viewport. When the
 * lockup, copy, and dock overflow, it stays in normal flow so the page
 * can scroll instead of shrinking to 100vh.
 */

const SELECTOR = '[data-stack]'

function viewportHeight() {
	return window.visualViewport?.height ?? window.innerHeight
}

function coverAlign(el) {
	const value = parseFloat(el.getAttribute('data-stack'))
	return Number.isFinite(value) ? value : 1
}

function pinExtent(el) {
	const align = coverAlign(el)
	const gutter = parseFloat(window.getComputedStyle(el).marginBottom) || 0

	if (align < 1) {
		const submit = el.querySelector(
			'.gform_button, .gform_footer button[type="submit"], .gform_footer input[type="submit"]',
		)
		if (submit) {
			return submit.getBoundingClientRect().bottom - el.getBoundingClientRect().top
		}
	}

	return el.offsetHeight + gutter
}

function pinOffset(el) {
	return Math.min(0, viewportHeight() * coverAlign(el) - pinExtent(el))
}

function shouldStack(el) {
	if (!el.classList.contains('section--hero')) return true
	return pinOffset(el) === 0
}

function pin(el) {
	if (!shouldStack(el)) {
		el.classList.remove('is-stacked')
		el.style.removeProperty('--stack-top')
		el.style.removeProperty('--stack-progress')
		return
	}

	el.style.setProperty('--stack-top', `${pinOffset(el)}px`)
	el.classList.add('is-stacked')
}

function layout(items) {
	items.forEach(({ el }) => pin(el))
}

function update(items) {
	items.forEach(({ el, next }) => {
		if (!next || !el.classList.contains('is-stacked')) return

		const overlap = el.getBoundingClientRect().bottom - next.getBoundingClientRect().top
		const progress = Math.min(Math.max(overlap / viewportHeight(), 0), 1)

		el.style.setProperty('--stack-progress', progress.toFixed(3))
	})
}

export default function initStack() {
	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

	const items = Array.from(document.querySelectorAll(SELECTOR))
		.map((el) => ({ el, next: el.nextElementSibling }))
		.filter((item) => item.next)

	if (!items.length) return

	layout(items)
	update(items)

	let ticking = false
	const onScroll = () => {
		if (ticking) return
		ticking = true
		requestAnimationFrame(() => {
			update(items)
			ticking = false
		})
	}

	window.addEventListener('scroll', onScroll, { passive: true })

	let resizeTimer = null
	const onResize = () => {
		window.clearTimeout(resizeTimer)
		resizeTimer = window.setTimeout(() => {
			layout(items)
			update(items)
		}, 150)
	}

	window.addEventListener('resize', onResize, { passive: true })
	if (window.visualViewport) {
		window.visualViewport.addEventListener('resize', onResize, { passive: true })
	}

	if (typeof ResizeObserver === 'function') {
		const observer = new ResizeObserver(onResize)
		items.forEach(({ el }) => observer.observe(el))
	}
}
